import React, { useState, useRef, Fragment, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Chip,
    IconButton,
    InputAdornment,
    Divider,
    Avatar,
} from "@mui/material";
import {
    ArrowBack,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import CommonButton from "../../common/components/CommonButton";
import PageHeader from "../../common/components/PageHeader";
import CommonCard from "../../common/components/CommonCard";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { setLoading } from "../../features/authSlice";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { getTicketDetails, sendMessageToTicket, updateTicket } from "../../api/supportTicketApi";
import moment from "moment";
import { TicketDisplayStatus, TicketStatus, UserTypeId } from "../../common/const";
import theme from "../../theme";

const TicketDetail = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { loading, user } = useSelector((state) => state.auth);
    const { ticketId } = useParams();
    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const [message, setMessage] = useState("");
    const [ticketData, setTicketData] = useState(null);
    const [messages, setMessages] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [avatarLoadError, setAvatarLoadError] = useState(false);

    const getMessageData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getTicketDetails(ticketId);
            setTicketData(response.data);
            const messageAttachmentIds = response?.data?.messages?.flatMap(message =>
                message.attachments?.map(att => att.attachment_id) || []
            ) || [];

            // Filter root attachments to only include those not found in message attachments
            const uniqueRootAttachments = response?.data?.attachments?.filter(rootAtt =>
                !messageAttachmentIds.includes(rootAtt.attachment_id)
            ) || [];

            const defaultMessage = {
                attachments: uniqueRootAttachments, // Only root attachments not in messages
                created_at: response?.data?.created_at,
                senderUser: {
                    email: response?.data?.studentUser?.email
                },
                message: response?.data?.description
            };
            setMessages([defaultMessage, ...response?.data?.messages]);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    useEffect(() => {
        getMessageData();
    }, [ticketId]);

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleBack = () => {
        navigate(-1);
    };

    const handleFileClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files || []);
        setUploadedFiles(files);
    };

    const handleRemoveFile = (index) => {
        fileInputRef.current.value = '';
        setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
    };

    const handleSendMessage = async () => {
        try {
            if (message.trim() || uploadedFiles.length > 0) {
                dispatch(setLoading({
                    loading: true
                }));
                const formData = new FormData();
                formData.append('message', message);
                formData.append('sender_type', user?.user_type_id === UserTypeId.Admin ? 'ADMIN' : 'STUDENT');
                if (uploadedFiles.length > 0) {
                    uploadedFiles.map((a) => {
                        formData.append('attachments', a);
                    });
                }
                const response = await sendMessageToTicket(ticketId, formData);
                if (response.success) {
                    setMessage("");
                    setUploadedFiles([]);
                    fileInputRef.current.value = '';
                    getMessageData();
                }
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSendMessage();
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + " B";
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
        return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    };

    useEffect(() => {
        setAvatarLoadError(false);
        setPreviewUrl(null);
        if (ticketData?.studentUser?.avatar) {
            const getImage = async () => {
                try {
                    const responseData = await fetch(`${process.env.REACT_APP_BASE_URL}/file?bname=${ticketData?.studentUser?.avatar}&download=false`, {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    });
                    if (!responseData.ok) {
                        setAvatarLoadError(true);
                        return;
                    }
                    const blob = await responseData.blob();
                    // Guard against the API returning an empty/invalid blob
                    if (!blob || blob.size === 0 || !blob.type.startsWith('image/')) {
                        setAvatarLoadError(true);
                        return;
                    }
                    const imageUrl = URL.createObjectURL(blob);
                    setPreviewUrl(imageUrl);
                } catch (e) {
                    setAvatarLoadError(true);
                }
            };
            getImage();
        }
    }, [ticketData]);

    // Helper: pull a clean first letter for the fallback avatar
    const getInitial = () => {
        const first = ticketData?.studentUser?.first_name?.trim();
        if (first) return first.charAt(0).toUpperCase();
        const last = ticketData?.studentUser?.last_name?.trim();
        if (last) return last.charAt(0).toUpperCase();
        const email = ticketData?.studentUser?.email?.trim();
        if (email) return email.charAt(0).toUpperCase();
        return '?';
    };

    // Helper: render the correct avatar for a given message
    const renderAvatar = (msg) => {
        const isAdmin = msg?.sender_type === 'ADMIN';

        if (isAdmin) {
            return (
                <img
                    src="/Images/dreamcollegepath-logo.png"
                    style={{
                        width: 40,
                        height: 40,
                        border: '1px solid #0000001A',
                        borderRadius: 8
                    }}
                    alt="support"
                />
            );
        }

        // Student / non-admin: show uploaded avatar if available and loaded
        if (previewUrl && !avatarLoadError) {
            return (
                <img
                    src={previewUrl}
                    style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        objectFit: 'cover',
                    }}
                    alt="user"
                    onError={() => setAvatarLoadError(true)}
                />
            );
        }

        // Fallback: first letter avatar
        return (
            <Avatar
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2, // matches the rounded-square style of the other avatars
                    bgcolor: theme.palette.secondary.main,
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: 18,
                }}
            >
                {getInitial()}
            </Avatar>
        );
    };

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}

            <PageHeader
                content={
                    <Box
                        sx={{
                            py: 2,
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                        }}
                        onClick={handleBack}
                    >
                        <ArrowBack sx={{ mr: 1, fontSize: 20 }} />
                        <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
                            Back
                        </Typography>
                    </Box>
                }
            />
            <CommonCard
                paddingBottom="0px"
                borderRadius={2}
                padding="0px"
                content={
                    <Fragment>
                        {/* Ticket Header */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                p: 2
                            }}
                        >
                            <Box>
                                <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {ticketData?.subject}
                                </Typography>
                                <Box display="flex" gap={2}>
                                    <Typography sx={{ color: "#757575", fontSize: 12 }}>
                                        {ticketData?.ticket_number}
                                    </Typography>
                                    <Typography sx={{ color: "#757575", fontSize: 12 }}>
                                        Created on {moment(ticketData?.created_at).format('DD MMM, YYYY')}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box display="flex" gap={1}>
                                {(ticketData?.status !== TicketStatus.Closed && user?.user_type_id === UserTypeId.Admin) && (
                                    <CommonButton
                                        label="Mark As Close"
                                        height="35px"
                                        fontWeight={600}
                                        marginBottom="0px"
                                        handleClick={async () => {
                                            try {
                                                dispatch(setLoading({
                                                    loading: true
                                                }));
                                                const response = await updateTicket(ticketId, {
                                                    status: TicketStatus.Closed
                                                });
                                                if (response.success) {
                                                    showNotification(response.message, 'success');
                                                    getMessageData();
                                                }
                                            } catch (err) {
                                                const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                                showNotification(errorMessage, 'error');
                                                dispatch(setLoading({
                                                    loading: false
                                                }));
                                            }
                                        }}
                                    />
                                )}
                                <Chip
                                    label={TicketDisplayStatus[ticketData?.status]}
                                    sx={{
                                        bgcolor: "#2e7d32",
                                        color: "white",
                                        fontWeight: 600,
                                        fontSize: "13px",
                                    }}
                                />
                            </Box>
                        </Box>
                        {ticketData?.status !== TicketStatus.Closed && (
                            <Divider />
                        )}
                        <Box
                            sx={{
                                pt: 1,
                                px: { xs: 2, md: 30 },
                                maxHeight: "calc(85vh - 250px)",
                                overflow: "auto",
                            }}
                        >
                            {messages.map((msg) => (
                                <Box
                                    key={msg?.support_message_id}
                                    sx={{ mb: 2 }}
                                >
                                    <Box sx={{ display: "flex", mb: 1, alignItems: 'center', gap: 1 }}>
                                        {renderAvatar(msg)}
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontWeight: 600, fontSize: 15 }}>
                                                {msg?.sender_type === 'ADMIN'
                                                    ? 'DCP Support Team'
                                                    : `${ticketData?.studentUser?.first_name} ${ticketData?.studentUser?.last_name || ''}`.trim()}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: "#757575", fontSize: 12 }}
                                            >
                                                {moment(msg?.created_at).format('MMM,DD, hh:mm A')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <CommonCard
                                        paddingBottom="0px"
                                        borderRadius={2}
                                        background={msg?.sender_type === 'ADMIN' ? '#DBF0FF' : "#FAFAFA"}
                                        border={msg?.sender_type === 'ADMIN' ? 'none' : `1px solid #0000001A`}
                                        content={
                                            <Fragment>
                                                {msg?.message && (
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 14,
                                                                lineHeight: 1.7,
                                                                color: "#424242",
                                                                mb: 1.5,
                                                                fontWeight: msg.id === 1 ? 600 : 400,
                                                            }}
                                                        >
                                                            {msg?.message}
                                                        </Typography>
                                                        {msg?.content?.description && (
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 14,
                                                                    lineHeight: 1.7,
                                                                    color: "#616161",
                                                                    mb: 2,
                                                                }}
                                                            >
                                                                {msg?.content?.description}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}

                                                {msg?.attachments?.length > 0 && (
                                                    <Box sx={{ mt: 2 }}>
                                                        {msg.attachments.map((file, index) => (
                                                            <Box
                                                                key={index}
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    p: 1.5,
                                                                    bgcolor: "white",
                                                                    borderRadius: 1,
                                                                    mb: 1,
                                                                    cursor: 'pointer',
                                                                    width: '20vw',
                                                                    border: '1px solid #0000001A'
                                                                }}
                                                                onClick={() => {
                                                                    window.open(`${process.env.REACT_APP_BASE_URL}/file?bname=${file?.file_url}&download=true`);
                                                                }}
                                                            >
                                                                <img src="/Images/sound.png" alt="upload" height="20px" style={{ marginRight: 5 }} />
                                                                <Typography sx={{ fontSize: 13, color: theme.palette.background.dark }}>
                                                                    {file?.file_name}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                            </Fragment>
                                        }
                                    />
                                </Box>
                            ))}
                            {/* Element to scroll to */}
                            <div ref={messagesEndRef} />
                        </Box>
                        <Box sx={{
                            flexShrink: 0,
                            px: { xs: 2, md: 30 },
                            py: 2
                        }}>
                            {uploadedFiles.length > 0 && (
                                <Box
                                    sx={{
                                        mb: 2,
                                        p: 2,
                                        borderBottom: "1px solid #e0e0e0",
                                        bgcolor: "#fafafa",
                                    }}
                                >
                                    {uploadedFiles.map((file, index) => (
                                        <Chip
                                            key={index}
                                            label={`${file.name} (${formatFileSize(file.size)})`}
                                            onDelete={() => handleRemoveFile(index)}
                                            sx={{ mr: 1, mb: 1 }}
                                            size="small"
                                        />
                                    ))}
                                </Box>
                            )}
                            {(ticketData?.status !== TicketStatus.Closed && user?.user_type_id !== UserTypeId.Parent) && (
                                <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        style={{ display: "none" }}
                                        onChange={handleFileChange}
                                        multiple
                                    />
                                    <TextField
                                        fullWidth
                                        placeholder="Type your reply here"
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        variant="outlined"
                                        size="small"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <CommonButton
                                                        label="Post"
                                                        marginBottom="0px"
                                                        fontWeight={600}
                                                        startIcon={<img src="/Images/Send.png" alt="send" height="15px" />}
                                                        disabled={!message?.trim() && uploadedFiles.length === 0}
                                                        handleClick={handleSendMessage}
                                                        height="35px"
                                                    />
                                                </InputAdornment>
                                            ),
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <IconButton onClick={handleFileClick}>
                                                        <img src="/Images/Attachment.png" alt="attachment" height="30px" />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                height: 50,
                                                backgroundColor: '#f5f5f5',
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                background: '#f5f5f5',
                                                'fieldset': {
                                                    border: 'none'
                                                },
                                                '&:hover fieldset': {
                                                    border: 'none',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    border: 'none'
                                                },
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Fragment>
                }
            />
        </Fragment>
    );
};

export default TicketDetail;